import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, CheckCircle, Clock, Shield, Award, Star, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import axios from 'axios';

// 🔒 Force l'appel vers l'API Vercel
const API = '/api';

const Homepage = () => {
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    ville: '',
    type_toiture: '',
    objet: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/devis`, formData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data?.ok) {
        toast({
          title: "Demande envoyée avec succès !",
          description: "Nous revenons vers vous sous 24–48h avec votre devis personnalisé.",
          variant: "default",
        });

        setFormData({
          nom: '',
          telephone: '',
          email: '',
          ville: '',
          type_toiture: '',
          objet: '',
          message: ''
        });
      } else {
        throw new Error(response.data?.error || 'Envoi impossible');
      }
    } catch (error) {
      console.error('Erreur envoi formulaire:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        "Une erreur est survenue. Veuillez réessayer.";

      toast({
        title: "Erreur d'envoi",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallClick = () => {
    // TODO: remplace par ton vrai numéro, ex: tel:+33612345678
    window.location.href = 'tel:[TEL]';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="hero section">
        <div className="hero-content">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left content */}
              <div className="space-y-8 fade-in">
                <div className="space-y-6">
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-tight text-white">
                    Couvreur professionnel à 
                    <span className="gradient-text block">
                      Toulouse & Occitanie
                    </span>
                  </h1>

                  <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
                    <strong className="text-blue-400">Devis gratuit sous 24-48h</strong> pour vos travaux de toiture, zinguerie et étanchéité.
                    Intervention rapide dans un rayon de 30km autour de Cugnaux.
                  </p>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-3">
                  <Badge className="glass text-white border-white/20 px-4 py-2">
                    <Shield className="w-4 h-4 mr-2" />
                    Décennale & RC Pro
                  </Badge>
                  <Badge className="glass text-white border-white/20 px-4 py-2">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Respect des DTU
                  </Badge>
                  <Badge className="glass text-white border-white/20 px-4 py-2">
                    <Clock className="w-4 h-4 mr-2" />
                    Réponse 24-48h
                  </Badge>
                  <Badge className="glass text-white border-white/20 px-4 py-2">
                    <Award className="w-4 h-4 mr-2" />
                    +15 ans d'expérience
                  </Badge>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact" className="btn btn-primary text-lg px-8 py-4">
                    Demander un devis gratuit
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <button 
                    type="button"
                    onClick={handleCallClick}
                    className="btn btn-ghost text-lg px-8 py-4 text-white hover:bg-white/10"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Appeler maintenant
                  </button>
                </div>
              </div>

              {/* Right form */}
              <div className="slide-up">
                <Card className="glass border-white/20 p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-2">
                      Devis gratuit en 24-48h
                    </h3>
                    <p className="text-gray-200">
                      Remplissez ce formulaire pour recevoir votre devis personnalisé
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        placeholder="Nom complet *"
                        value={formData.nom}
                        onChange={(e) => handleInputChange('nom', e.target.value)}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-300"
                      />
                      <Input
                        type="tel"
                        placeholder="Téléphone *"
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-300"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        type="email"
                        placeholder="Email *"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-300"
                      />
                      <Input
                        placeholder="Ville *"
                        value={formData.ville}
                        onChange={(e) => handleInputChange('ville', e.target.value)}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-300"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Select
                        value={formData.type_toiture}
                        onValueChange={(value) => handleInputChange('type_toiture', value)}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Type de toiture" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tuiles">Tuiles</SelectItem>
                          <SelectItem value="ardoises">Ardoises</SelectItem>
                          <SelectItem value="bac-acier">Bac acier</SelectItem>
                          <SelectItem value="toit-terrasse">Toit-terrasse</SelectItem>
                          <SelectItem value="autres">Autres</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select
                        value={formData.objet}
                        onValueChange={(value) => handleInputChange('objet', value)}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Type de prestation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="refection">Réfection toiture</SelectItem>
                          <SelectItem value="zinguerie">Zinguerie</SelectItem>
                          <SelectItem value="etancheite">Étanchéité</SelectItem>
                          <SelectItem value="nettoyage">Nettoyage/Démoussage</SelectItem>
                          <SelectItem value="depannage">Dépannage urgence</SelectItem>
                          <SelectItem value="devis-general">Devis général</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Textarea
                      placeholder="Décrivez votre projet (optionnel)"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={3}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-300"
                    />

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full btn-primary text-lg py-4"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Recevoir mon devis sous 24-48h'}
                    </Button>

                    <p className="text-xs text-gray-300 text-center">
                      Devis gratuit ≤30 km autour de Cugnaux. Intervention à signature du devis.
                      <br />
                      En soumettant ce formulaire, j'accepte d'être contacté pour mon devis.
                    </p>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="container">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
              <Star className="w-5 h-5 fill-current text-yellow-500" />
              <span>4,8/5 (97 avis)</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
              <Shield className="w-5 h-5 text-blue-600" />
              <span>Décennale & RC Pro</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Respect des DTU</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
              <Clock className="w-5 h-5 text-orange-600" />
              <span>Intervention sous 48h</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-['Outfit'] mb-4">
              La solidité d'un artisan local
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des chiffres qui témoignent de notre expertise et de la confiance de nos clients
            </p>
          </div>

          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">+15</div>
              <div className="stat-label">années d'expérience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">4,8/5</div>
              <div className="stat-label">note moyenne clients</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">48h</div>
              <div className="stat-label">délai de réponse</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">devis détaillés</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-16">
            <div className="text-blue-600 font-semibold text-lg mb-4">
              Couverture · Zinguerie · Étanchéité · Dépannage
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-['Outfit'] mb-4">
              Nos expertises
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez notre gamme complète de services pour tous vos besoins en couverture et zinguerie
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              { title: "Réfection de toiture", desc: "Rénovation complète avec matériaux de qualité" },
              { title: "Zinguerie", desc: "Gouttières, noues et abergements sur mesure" },
              { title: "Étanchéité", desc: "Solutions EPDM et bitume pour toits-terrasses" },
              { title: "Nettoyage & démoussage", desc: "Entretien professionnel de votre toiture" },
              { title: "Dépannage 24/7", desc: "Interventions d'urgence toute l'année" },
              { title: "Isolation", desc: "Amélioration thermique et phonique" }
            ].map((service, index) => (
              <Card key={index} className="card-hover p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 font-['Outfit']">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.desc}
                </p>
                <Link 
                  to="/services"
                  className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
                >
                  En savoir plus
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services" className="btn btn-primary">
              Voir tous nos services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-blue-600 text-white">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
            Prêt à démarrer votre projet ?
          </h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto">
            Obtenez votre devis gratuit en moins de 24h et bénéficiez de notre expertise locale
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn bg-white text-blue-600 hover:bg-gray-100">
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button 
              type="button"
              onClick={handleCallClick}
              className="btn btn-ghost text-white border-white hover:bg-white/10"
            >
              <Phone className="w-5 h-5 mr-2" />
              Nous appeler
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Homepage;
